import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import CoverMediaEditor from '../components/CoverMediaEditor';
import ProductPicker from '../components/ProductPicker';
import ShopMediaPreview from '../components/ShopMediaPreview';
import ShopProductFields from '../components/ShopProductFields';
import {
  BUNDLE_COMPONENT_TYPES,
  BUNDLE_SLOTS,
  BundleItem,
  BundleSlotKey,
  bundleActivationIssues,
  bundleItemsFromProduct,
  buildPremiumBundlePayload,
  CandidateProduct,
  COVER_SLOT_TYPES,
  emptyShopProductForm,
  Environment,
  formFromProduct,
  hasMedia,
  MAX_BUNDLE_ITEMS,
  moveBundleItem,
  PREMIUM_BUNDLE_TYPE,
  productTypeLabel,
  removeBundleItem,
  ResolvedBundleItem,
  ShopMediaAsset,
  ShopProductForm,
  slotByKey,
  slotForType,
  THEMES_DECOR_CATEGORY,
  upsertSlotItem,
} from '../utils/premiumBundleRules';
import { authHeaders, deleteWalletShopProduct, fetchProductsByType, saveWalletShopProduct } from '../utils/walletShopApi';

type ComponentStatus = { tone: 'ok' | 'warn' | 'error'; text: string };

/**
 * Create / edit a premium bundle (`premium_bundle`): one purchasable item granting up to six
 * existing products, one per slot. Route: /products/premium-bundles/new and
 * /products/premium-bundles/edit?productKey=…&env=…
 */
const PremiumBundleEditor: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productKeyParam = (searchParams.get('productKey') || '').trim().toUpperCase();
  const isUpdateMode = !!productKeyParam;
  const envParam: Environment = (searchParams.get('env') || '').toLowerCase() === 'dev' ? 'dev' : 'prod';

  const [form, setForm] = useState<ShopProductForm>(() => ({ ...emptyShopProductForm(envParam), ProductKey: productKeyParam }));
  const [items, setItems] = useState<BundleItem[]>([]);
  const [resolvedItems, setResolvedItems] = useState<ResolvedBundleItem[]>([]);
  const [mediaAssets, setMediaAssets] = useState<ShopMediaAsset[]>([]);
  const [candidates, setCandidates] = useState<CandidateProduct[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(isUpdateMode);
  const [notFound, setNotFound] = useState(false);
  const [openSlot, setOpenSlot] = useState<BundleSlotKey | ''>('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const patchForm = (patch: Partial<ShopProductForm>) => setForm((prev) => ({ ...prev, ...patch }));
  const backToList = useCallback(
    (env: Environment) => navigate(`/products?category=${THEMES_DECOR_CATEGORY}&type=${PREMIUM_BUNDLE_TYPE}&env=${env}`),
    [navigate]
  );

  // Existing bundle (edit) or next DisplayOrder (create).
  useEffect(() => {
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      setLoadingProduct(false);
      return;
    }
    let cancelled = false;
    fetchProductsByType<Record<string, any>>(auth.headers, PREMIUM_BUNDLE_TYPE, envParam)
      .then((bundles) => {
        if (cancelled) return;
        if (!isUpdateMode) {
          const maxOrder = bundles.reduce((max, b) => Math.max(max, Number(b.DisplayOrder) || 0), 0);
          setForm((prev) => (prev.DisplayOrder ? prev : { ...prev, DisplayOrder: String(maxOrder + 1) }));
          return;
        }
        const existing = bundles.find((b) => b.ProductKey === productKeyParam);
        if (!existing) {
          setNotFound(true);
          return;
        }
        setForm(formFromProduct(existing, envParam));
        setItems(bundleItemsFromProduct(existing));
        setResolvedItems(existing.BundleItems || []);
        const assets: ShopMediaAsset[] = existing.MediaAssets || [];
        setMediaAssets(assets.filter((a) => (COVER_SLOT_TYPES as string[]).includes(a.Type)));
      })
      .catch((error) => !cancelled && toast.error((error as Error).message || 'Failed to load premium bundles'))
      .finally(() => !cancelled && setLoadingProduct(false));
    return () => {
      cancelled = true;
    };
  }, [envParam, isUpdateMode, productKeyParam]);

  // Candidate components for the bundle's environment (inactive included).
  useEffect(() => {
    const auth = authHeaders();
    if ('error' in auth) return;
    let cancelled = false;
    setLoadingCandidates(true);
    Promise.allSettled(BUNDLE_COMPONENT_TYPES.map((type) => fetchProductsByType<CandidateProduct>(auth.headers, type, form.Environment)))
      .then((results) => {
        if (cancelled) return;
        const loaded: CandidateProduct[] = [];
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') loaded.push(...result.value);
          else toast.warn(`Couldn't load ${productTypeLabel(BUNDLE_COMPONENT_TYPES[index])} products: ${result.reason?.message || 'error'}`);
        });
        setCandidates(loaded);
      })
      .finally(() => !cancelled && setLoadingCandidates(false));
    return () => {
      cancelled = true;
    };
  }, [form.Environment]);

  const candidateLookup = useMemo(() => new Map(candidates.map((c) => [c.ProductKey, c])), [candidates]);
  const resolvedLookup = useMemo(() => new Map(resolvedItems.map((r) => [r.ProductKey, r])), [resolvedItems]);

  const { payload, issues } = useMemo(
    () => buildPremiumBundlePayload(form, items, mediaAssets, candidateLookup),
    [form, items, mediaAssets, candidateLookup]
  );
  // While drafting, still show what would block activation.
  const activationPreview = useMemo(
    () => (form.IsActive ? [] : bundleActivationIssues(items, candidateLookup, form.Environment)),
    [form.IsActive, form.Environment, items, candidateLookup]
  );
  const blockedByLoading = form.IsActive && loadingCandidates;

  const componentStatus = (item: BundleItem): ComponentStatus => {
    if (loadingCandidates) return { tone: 'warn', text: 'Checking...' };
    const product = candidateLookup.get(item.ProductKey);
    if (!product) return { tone: item.Required ? 'error' : 'warn', text: `Missing in ${form.Environment}` };
    if (product.Type !== item.Type) return { tone: item.Required ? 'error' : 'warn', text: `Wrong type (${productTypeLabel(product.Type)})` };
    if (!hasMedia(product.MediaAssets)) return { tone: item.Required ? 'error' : 'warn', text: 'No media' };
    return { tone: 'ok', text: 'Ready' };
  };

  const pickComponent = (candidate: CandidateProduct) => {
    try {
      setItems((prev) => upsertSlotItem(prev, candidate));
      setOpenSlot('');
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleSave = async () => {
    if (issues.length) {
      toast.error(issues[0]);
      return;
    }
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      return;
    }
    setSaving(true);
    try {
      const json = await saveWalletShopProduct(auth.headers, payload, isUpdateMode);
      toast.success(json.message || (form.IsActive ? 'Premium bundle saved and active' : 'Premium bundle draft saved'));
      backToList(form.Environment);
    } catch (error) {
      // Server activation errors name the problem component — surface them verbatim.
      toast.error((error as Error).message || 'Failed to save premium bundle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete premium bundle ${form.ProductKey} (${form.Environment})? Its components are not deleted.`)) return;
    const auth = authHeaders();
    if ('error' in auth) {
      toast.error(auth.error);
      return;
    }
    setDeleting(true);
    try {
      const json = await deleteWalletShopProduct(auth.headers, form.ProductKey, form.Environment);
      toast.success(json.message || 'Premium bundle deleted');
      backToList(form.Environment);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete premium bundle');
    } finally {
      setDeleting(false);
    }
  };

  const filledSlots = new Set(items.map((item) => slotForType(item.Type)?.key));
  const emptySlots = BUNDLE_SLOTS.filter((slot) => !filledSlots.has(slot.key));
  const toneClass = { ok: 'text-emerald-300 border-emerald-400/40', warn: 'text-amber-300 border-amber-400/40', error: 'text-red-300 border-red-400/40' };

  const renderPicker = (slotKey: BundleSlotKey, currentKey?: string) => {
    const slot = slotByKey(slotKey);
    return (
      <div className='mt-2 bg-black/30 border border-white/10 rounded-lg p-3 space-y-2'>
        <div className='flex items-center justify-between'>
          <span className='text-xs text-gray-300'>
            Pick a {slot.label.toLowerCase()} ({slot.types.map(productTypeLabel).join(' or ')}) from {form.Environment}
          </span>
          <button type='button' onClick={() => setOpenSlot('')} className='text-xs text-gray-400 hover:text-white'>
            Close
          </button>
        </div>
        <ProductPicker
          candidates={candidates.filter((c) => slot.types.includes(c.Type))}
          loading={loadingCandidates}
          selectedKeys={new Set(currentKey ? [currentKey] : [])}
          onPick={pickComponent}
          emptyText={`No ${slot.label.toLowerCase()} products in ${form.Environment}.`}
        />
      </div>
    );
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between gap-4'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>{isUpdateMode ? 'Update Premium Bundle' : 'Create Premium Bundle'}</h1>
            <p className='text-sm text-gray-400'>
              One purchase that grants a frame, background, chat theme, sticker pack, entry animation and profile animation. Shown under Wallet Shop →
              Themes &amp; Decor → Premium Packs.
            </p>
          </div>
          <button
            onClick={() => backToList(form.Environment)}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 shrink-0'
          >
            Back to Assets
          </button>
        </div>

        {loadingProduct ? (
          <div className='text-sm text-gray-400'>Loading premium bundle...</div>
        ) : notFound ? (
          <div className='p-6 bg-white/5 border border-white/10 rounded-2xl text-sm text-gray-300'>
            Premium bundle {productKeyParam} was not found in {envParam}.
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <div className='space-y-6'>
              <section className='bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4'>
                <div>
                  <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 1</p>
                  <h2 className='text-lg font-semibold text-white'>Components</h2>
                  <p className='text-xs text-gray-400 mt-1'>
                    Up to {MAX_BUNDLE_ITEMS}, one per slot. The bundle references these products — it doesn't copy them. Inactive
                    (bundle-exclusive) components are allowed.
                  </p>
                </div>

                {items.length === 0 && <p className='text-xs text-gray-500'>No components yet.</p>}
                {items.map((item, index) => {
                  const slot = slotForType(item.Type);
                  const product = candidateLookup.get(item.ProductKey);
                  const resolved = resolvedLookup.get(item.ProductKey);
                  const status = componentStatus(item);
                  return (
                    <div key={`${item.Type}-${item.ProductKey}`} className='bg-black/20 border border-white/10 rounded-lg p-3'>
                      <div className='flex items-start gap-3'>
                        <ShopMediaPreview assets={product?.MediaAssets || resolved?.MediaAssets} size={64} />
                        <div className='min-w-0 flex-1 space-y-1'>
                          <div className='text-[11px] uppercase tracking-wide text-[#FF9C15]'>
                            {index + 1}. {slot?.label || item.Type}
                          </div>
                          <div className='text-sm font-semibold text-white truncate'>{product?.Name || resolved?.Name || item.ProductKey}</div>
                          <div className='text-[11px] text-gray-400 font-mono truncate'>
                            {item.ProductKey} · {productTypeLabel(item.Type)}
                          </div>
                          <div className='flex flex-wrap items-center gap-2'>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${toneClass[status.tone]}`}>{status.text}</span>
                            {product && (
                              <span className={`text-[10px] ${product.IsActive === false ? 'text-gray-400' : 'text-emerald-300'}`}>
                                {product.IsActive === false ? 'Inactive (bundle-only)' : 'Active'}
                              </span>
                            )}
                            <label className='flex items-center gap-1 text-[11px] text-gray-200 cursor-pointer'>
                              <input
                                type='checkbox'
                                checked={item.Required}
                                onChange={(e) =>
                                  setItems((prev) => prev.map((it, i) => (i === index ? { ...it, Required: e.target.checked } : it)))
                                }
                                className='accent-[#FF9C15]'
                              />
                              Required
                            </label>
                          </div>
                        </div>
                        <div className='flex flex-col gap-1 shrink-0'>
                          <button
                            type='button'
                            aria-label='Move up'
                            disabled={index === 0}
                            onClick={() => setItems((prev) => moveBundleItem(prev, index, -1))}
                            className='px-2 py-0.5 text-xs rounded bg-white/10 text-white hover:bg-white/20 disabled:opacity-30'
                          >
                            ↑
                          </button>
                          <button
                            type='button'
                            aria-label='Move down'
                            disabled={index === items.length - 1}
                            onClick={() => setItems((prev) => moveBundleItem(prev, index, 1))}
                            className='px-2 py-0.5 text-xs rounded bg-white/10 text-white hover:bg-white/20 disabled:opacity-30'
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                      <div className='flex gap-3 mt-2'>
                        {slot && (
                          <button
                            type='button'
                            onClick={() => setOpenSlot(openSlot === slot.key ? '' : slot.key)}
                            className='text-xs text-sky-300 hover:text-sky-200'
                          >
                            Change
                          </button>
                        )}
                        <button
                          type='button'
                          onClick={() => setItems((prev) => removeBundleItem(prev, index))}
                          className='text-xs text-red-300 hover:text-red-200'
                        >
                          Remove
                        </button>
                      </div>
                      {slot && openSlot === slot.key && renderPicker(slot.key, item.ProductKey)}
                    </div>
                  );
                })}

                {emptySlots.length > 0 && (
                  <div className='space-y-2'>
                    <div className='flex flex-wrap gap-2'>
                      {emptySlots.map((slot) => (
                        <button
                          type='button'
                          key={slot.key}
                          onClick={() => setOpenSlot(openSlot === slot.key ? '' : slot.key)}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                            openSlot === slot.key
                              ? 'border-[#FF9C15] text-white bg-white/10'
                              : 'border-white/20 text-gray-300 hover:text-white hover:border-white/40'
                          }`}
                        >
                          + {slot.label}
                        </button>
                      ))}
                    </div>
                    {emptySlots.some((slot) => slot.key === openSlot) && renderPicker(openSlot as BundleSlotKey)}
                  </div>
                )}
              </section>

              <section className='bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4'>
                <div>
                  <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 2</p>
                  <h2 className='text-lg font-semibold text-white'>Cover</h2>
                </div>
                <CoverMediaEditor productKey={form.ProductKey} mediaAssets={mediaAssets} onChange={setMediaAssets} />
              </section>
            </div>

            <section className='bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4 h-fit'>
              <div>
                <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 3</p>
                <h2 className='text-lg font-semibold text-white'>Bundle Details</h2>
              </div>
              <ShopProductFields
                form={form}
                onChange={patchForm}
                isUpdateMode={isUpdateMode}
                themePreferenceHelp='Optional. Not set: each component keeps its own Theme Preference.'
              />

              {issues.length > 0 && (
                <div className='bg-red-500/10 border border-red-400/40 rounded-lg px-3 py-2 text-xs text-red-200 space-y-1'>
                  <div className='font-semibold'>{form.IsActive ? "Can't save as active yet:" : "Can't save yet:"}</div>
                  <ul className='list-disc pl-4 space-y-0.5'>
                    {issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!form.IsActive && activationPreview.length > 0 && (
                <div className='bg-amber-500/10 border border-amber-400/40 rounded-lg px-3 py-2 text-xs text-amber-200 space-y-1'>
                  <div className='font-semibold'>Draft — fix these before activating:</div>
                  <ul className='list-disc pl-4 space-y-0.5'>
                    {activationPreview.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={handleSave}
                disabled={saving || deleting || issues.length > 0 || blockedByLoading}
                className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
              >
                {saving
                  ? 'Saving...'
                  : blockedByLoading
                    ? 'Checking components...'
                    : form.IsActive
                      ? isUpdateMode
                        ? 'Update & Keep Active'
                        : 'Create Active Bundle'
                      : 'Save Draft (Inactive)'}
              </button>
              {isUpdateMode && (
                <button
                  onClick={handleDelete}
                  disabled={saving || deleting}
                  className='w-full py-2 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-60'
                >
                  {deleting ? 'Deleting...' : 'Delete Bundle'}
                </button>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumBundleEditor;
